/**
 * CrossModuleVoiceActions — globally-registered voice actions.
 *
 * Sits inside <VoiceActionsProvider> and registers navigation
 * actions for the modules that don't yet have per-page registration:
 *  • HustleUp (groups, my events, organisers)
 *  • Property / Real Estate (find a home, my watchlist)
 *  • Holidays (find a hotel, my bookings)
 *  • HustleShop (browse, featured)
 *  • Plaza / Eats / Tackle / Directory / Emergency / Voice
 *
 * One useEffect registers all actions on mount, unregisters on unmount
 * — fully hooks-rules-compliant.
 */
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useVoiceActions } from "@/context/VoiceActionsContext";

const ACTIONS = [
  // HustleUp
  { phrases: ["hustle up", "open hustle up", "open meetups", "find groups"],
    href: "/hustleup", label: "Open HustleUp" },
  { phrases: ["hustle up groups", "my hustle up groups", "show my groups"],
    href: "/hustleup/groups", label: "HustleUp Groups" },
  { phrases: ["my hustle up", "my events", "what events am i going to"],
    href: "/hustleup/me", label: "My HustleUp" },
  { phrases: ["create a group", "new hustle up group"],
    href: "/hustleup/groups/new", label: "Create group" },

  // Property / Real Estate
  { phrases: ["property", "real estate", "hustle property", "open property"],
    href: "/property", label: "Open Property" },
  { phrases: ["find a home", "match me a home", "match property", "find me a property"],
    href: "/property/find", label: "Match Me a Property" },

  // Holidays
  { phrases: ["holidays", "hustle holidays", "open holidays"],
    href: "/hustleholidays", label: "Open Holidays" },
  { phrases: ["find a hotel", "hustle hotels", "book a stay"],
    href: "/hustleholidays/hotels", label: "Find Hotels" },

  // HustleShop
  { phrases: ["hustle shop", "open hustle shop", "shop", "open shop"],
    href: "/hustleshop", label: "Open HustleShop" },
  { phrases: ["featured products", "hustle shop featured", "show featured"],
    href: "/hustleshop?featured=1", label: "Featured products" },

  // Plaza
  { phrases: ["hustle plaza", "open plaza", "marketplace", "shop plaza"],
    href: "/hustleplaza", label: "Open Hustle Plaza" },

  // Eats
  { phrases: ["hustle eats", "open eats", "find restaurants", "restaurants near me"],
    href: "/hustleeats", label: "Open Hustle Eats" },
  { phrases: ["scan table", "open scanner", "scan qr"],
    href: "/hustleeats/scan", label: "Scan Table" },

  // Tackle / Fish Map
  { phrases: ["hustle tackle", "open tackle"],
    href: "/tackle", label: "Open Tackle" },
  { phrases: ["fish map", "hustle fish map", "where to fish"],
    href: "/tackle/fish-map", label: "Open Fish Map" },

  // Directory / Utilities
  { phrases: ["hustle directory", "directory", "open directory"],
    href: "/hustle-directory", label: "Open Directory" },
  { phrases: ["hustle toilets", "find a toilet", "public toilet"],
    href: "/hustle-directory?q=toilets", label: "Find Toilets" },
  { phrases: ["hustle transport", "buses near me", "public transport"],
    href: "/hustle-directory?q=transport", label: "Find Transport" },

  // Emergency
  { phrases: ["hustle emergency", "emergency", "open emergency", "i need help"],
    href: "/hustle-emergency", label: "Emergency dial pad" },

  // Voice / Account
  { phrases: ["voice settings", "change voice phrases", "customise voice"],
    href: "/voice/settings", label: "Voice settings" },
  { phrases: ["my account", "account settings", "privacy settings", "delete my account"],
    href: "/account/settings", label: "Account settings" },

  // Suite modules — "open <module>" navigation
  { phrases: ["open suite", "hustle suite", "open the suite"],
    href: "/", label: "Open HustleSuite" },
  { phrases: ["open chat", "hustle chat", "open messages"],
    href: "/hustlechat", label: "Open HustleChat" },
  { phrases: ["open deals", "hustle deals", "show me deals"],
    href: "/deals", label: "Open HustleDeals" },
  { phrases: ["open rewards", "hustle rewards", "my rewards", "check my rewards"],
    href: "/account/credits", label: "Open HustleRewards" },
  { phrases: ["open diy", "hustle diy", "sell my home", "diy house sale"],
    href: "/property", label: "Open HustleDIY" },
  { phrases: ["open campus", "hustle campus", "my courses"],
    href: "/campus", label: "Open HustleCampus" },
  { phrases: ["open press", "hustle press", "local news"],
    href: "/hustlepress", label: "Open HustlePress" },
  { phrases: ["open recipes", "hustle recipes", "find a recipe"],
    href: "/recipes", label: "Open HustleRecipes" },
  { phrases: ["open stays", "hustle stays", "open bnb", "hustle bnb"],
    href: "/stays", label: "Open HustleStays" },
  { phrases: ["open connect", "hustle connect"],
    href: "/connect", label: "Open HustleConnect" },
  { phrases: ["open reviews", "hustle reviews", "read reviews"],
    href: "/reviews", label: "Open HustleReviews" },
  { phrases: ["open opinions", "hustle opinions", "have my say"],
    href: "/hustlepress", label: "Open HustleOpinions" },
];

export default function CrossModuleVoiceActions() {
  const navigate = useNavigate();
  const { register, unregister } = useVoiceActions();

  useEffect(() => {
    const ids = ACTIONS.map((a) =>
      register({
        phrases: a.phrases,
        label: a.label,
        scope: "global",
        handler: () => {
          try { navigate(a.href); } catch (e) { console.warn(e); }
        },
      })
    );
    return () => {
      for (const id of ids) {
        try { unregister(id); } catch {}
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [register, unregister, navigate]);

  return null;
}
